import { ReactNode, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/classes";
import { TooltipPosition, ARROW_STYLES, TRANSLATE_CLASSES, TRANSFORM_ORIGINS, calculateCoords } from "./tooltip.constants";

/* Prop Types */
interface TooltipProps {
  children: ReactNode;
  content?: ReactNode;
  contentClassName?: string;
  position?: TooltipPosition;
  className?: string;
  showArrow?: boolean;
}

/*
 * Tooltip component
 * Displays a tooltip on hover using a portal to avoid overflow clipping.
 */
export const Tooltip = ({ children, content, position = "top", className, contentClassName, showArrow = true }: TooltipProps) => {
  /* State */
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  /* Refs */
  const triggerRef = useRef<HTMLDivElement>(null);

  /* Handlers */
  const handleMouseEnter = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords(calculateCoords(rect, position));
    setIsVisible(true);
  }, [position]);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  /* Early return */
  if (!content) return <>{children}</>;

  /* Render */
  return (
    <>
      <div ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={className}>
        {children}
      </div>
      {isVisible &&
        coords &&
        createPortal(
          <div
            role="tooltip"
            className={cn(
              "fixed z-[9999] bg-base-100 rounded-md shadow-lg animate-in fade-in zoom-in-95 duration-150 px-2 py-1 text-sm",
              TRANSLATE_CLASSES[position],
              showArrow && ARROW_STYLES[position],
              contentClassName,
            )}
            style={{ top: coords.top, left: coords.left, transformOrigin: TRANSFORM_ORIGINS[position] }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};
